import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription } from 'rxjs';
import {
  DYNABOT_MAX_MESSAGE_LENGTH,
  DYNABOT_MAX_MESSAGES,
  DynabotChatMessage,
  DynabotContextSectionSummary,
  SendDynabotMessageFn,
} from '@portal-workspace/grow-shared-library';

/**
 * Floating DynaBot bubble and chat panel. The host decides whether it is shown at all (internal
 * user, feature flag) and which application is open; this component only runs the conversation.
 * The conversation lives here and nowhere else, so it is gone on reload or when the application
 * changes.
 */
@Component({
  selector: 'dynabot-widget',
  templateUrl: './dynabot-widget.component.html',
  styleUrls: ['./dynabot-widget.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
})
export class DynabotWidgetComponent implements OnChanges, OnDestroy {

  @Input() applicationId: number | null = null;
  @Input({ required: true }) sendDynabotMessageFn!: SendDynabotMessageFn;

  @ViewChild('messageList') messageList?: ElementRef<HTMLElement>;

  readonly maxLength = DYNABOT_MAX_MESSAGE_LENGTH;
  readonly formControlMessage = new FormControl<string>('', { nonNullable: true });

  open = false;
  messages: DynabotChatMessage[] = [];
  pending = false;
  errorMessage: string | null = null;
  noticeMessage: string | null = null;
  contextBuiltAt: string | null = null;
  contextSections: DynabotContextSectionSummary[] = [];
  showSources = false;

  private sessionId: string | null = null;
  private sendSubscription: Subscription | null = null;
  private readonly cdr = inject(ChangeDetectorRef);

  get hasApplication(): boolean {
    return this.applicationId != null;
  }

  get canSend(): boolean {
    return this.hasApplication && !this.pending && this.formControlMessage.value.trim().length > 0 && !this.conversationFull;
  }

  get conversationFull(): boolean {
    return this.messages.length >= DYNABOT_MAX_MESSAGES - 1;
  }

  get sourcesLabel(): string {
    const loaded = this.contextSections.filter(s => s.status === 'loaded').length;
    return `${loaded} of ${this.contextSections.length} sources loaded`;
  }

  sourceIcon(section: DynabotContextSectionSummary): string {
    return section.status === 'loaded' ? 'check_circle' : section.status === 'empty' ? 'remove_circle_outline' : 'error';
  }

  toggleSources(): void {
    this.showSources = !this.showSources;
  }

  get pendingLabel(): string {
    return this.sessionId ? 'DynaBot is thinking…' : 'DynaBot is reading this application. The first answer can take a couple of minutes while bank statements load…';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applicationId'] && !changes['applicationId'].firstChange
      && changes['applicationId'].previousValue !== changes['applicationId'].currentValue) {
      this.resetChat();
    }
  }

  ngOnDestroy(): void {
    this.sendSubscription?.unsubscribe();
  }

  toggle(): void {
    this.open = !this.open;
  }

  resetChat(): void {
    this.sendSubscription?.unsubscribe();
    this.sendSubscription = null;
    this.messages = [];
    this.sessionId = null;
    this.contextBuiltAt = null;
    this.contextSections = [];
    this.showSources = false;
    this.pending = false;
    this.errorMessage = null;
    this.noticeMessage = null;
    this.cdr.markForCheck();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  send(): void {
    if (!this.canSend || this.applicationId == null) {
      return;
    }
    const content = this.formControlMessage.value.trim();
    this.formControlMessage.setValue('');
    this.messages = [...this.messages, { role: 'user', content }];
    this.pending = true;
    this.errorMessage = null;
    this.noticeMessage = null;
    this.scrollToBottomSoon();

    this.sendSubscription = this.sendDynabotMessageFn({
      applicationId: this.applicationId,
      sessionId: this.sessionId,
      messages: this.messages,
    }).subscribe({
      next: response => {
        this.pending = false;
        if (response.status && response.payload) {
          this.sessionId = response.payload.sessionId;
          this.contextBuiltAt = response.payload.contextBuiltAt ?? this.contextBuiltAt;
          this.contextSections = response.payload.contextSections ?? this.contextSections;
          this.messages = [...this.messages, { role: 'assistant', content: response.payload.reply }];
        } else {
          this.errorMessage = response.message || 'DynaBot could not answer right now.';
        }
        this.cdr.markForCheck();
        this.scrollToBottomSoon();
      },
      error: (err: HttpErrorResponse) => {
        this.pending = false;
        if (err.status === 409) {
          // The server-side snapshot is gone; keep the transcript on screen but start a fresh session.
          this.sessionId = null;
          this.contextBuiltAt = null;
          this.contextSections = [];
          this.messages = this.messages.slice(0, -1);
          this.formControlMessage.setValue(content);
          this.noticeMessage = 'This chat expired, so DynaBot will reload the application on your next message.';
        } else {
          this.errorMessage = err.error?.message || 'DynaBot could not answer right now. Please try again.';
        }
        this.cdr.markForCheck();
      },
    });
  }

  private scrollToBottomSoon(): void {
    setTimeout(() => {
      const el = this.messageList?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
